import type { DatevError } from "@/components/shared/errors/datev-error-message";
import type { AccountEntityType } from "@/lib/constants/account-types";
import type {
  InvoiceSmbReviewStatus,
  InvoiceStatus,
  InvoiceType,
} from "@/lib/constants/invoiceStatus";
import type { InvoiceReviewType } from "@/lib/constants/invoices";
import type { TableInvoiceFragment } from "@/queries/invoices/graphql";

export interface IInputAccountUpdate {
  accountId: string;
  updatedFields: Partial<AccountDetails>;
}

// ***************** Query Types  *****************

export type InvoiceQueryVariables = {
  invoiceId: string; // UUID type
};

export type CostCenters = {
  costCenterId: string;
  name: string;
  description: string;
  identifier: string;
};

export enum InvoiceTagsSource {
  automatic = "automatic",
  manual = "manual",
}

export interface IAddressValidityDetails {
  companyRecipientNameAnswer: boolean;
  companyRecipientNameReasoning: string;
  companyRecipientAddressAnswer: boolean;
  companyRecipientAddressReasoning: string;
  companyRecipientVatIdAnswer: boolean;
  companyRecipientVatIdReasoning: string;
}

export type TagDetails = {
  source: InvoiceTagsSource;
  details?: IAddressValidityDetails | null;
};

export type InvoiceTag = {
  tagId: string; // UUID type
  tagName: string;
  llmRule?: string;
  details: TagDetails | null;
  approval?: boolean;
  createdAt?: string;
  updatedAt?: string;
};

export type InvoiceQueryResponse = {
  invoice: InvoiceData;
};

export type ExportHistoryType = {
  datevExportId: string; // UUID type
  exportedAt: Date;
  invoiceIds: string[]; // UUID type
  exportType: string;
  stackName: string | null;
  results: {
    errors: {
      validation_details: DatevError;
      code?: string;
      data: {
        error: string;
        error_uri?: string;
      };
    };
  };
};

export enum InvoiceSource {
  email = "email",
  upload = "upload",
}

export type InvoiceData = {
  errorReason?: {
    code?: string;
    data?: any;
    message?: string;
  };
  validationErrors?: Record<string, string>[];
  supplierName: string;
  tenantId: string;
  buJustification?: string;
  fileName: string;
  serviceDate: Date;
  serviceDateConfidence: number | null;
  processingStatus: string;
  documentTypeSummary: string;
  vatIdConfidence: number | null;
  type: InvoiceType;

  bookingPeriod: Date;
  invoiceId: string; // UUID type
  invoiceText: string;
  aiSummary: string;
  totalAmount: number;
  currency: string;
  dueDate: Date;
  invoiceDate: Date;
  invoiceDateConfidence: number | null;
  invoiceNo: string;
  invoiceNoConfidence: number | null;
  invoiceSource: string;
  fileBlobName: string;
  supplierId: string | null; // UUID type
  status: InvoiceStatus;
  createdAt: Date;
  lineItems: LineItem[];
  isNettoInvoice: boolean;
  supplier: Supplier | null;
  similarInvoices: InvoiceData[];
  splitPdf: boolean;
  supplierMatch: boolean;
  supplierMatchConfidence: number | null;
  totalAmountConfidence: number | null;
  polygons: Polygon[];
  split: boolean;
  reviewType: InvoiceReviewType;
  ocrReviewed: boolean;
  createDatevPayment: boolean;
  isDigitalInvoice?: boolean;
  isBooking: boolean;
  isDuplicate: boolean;
  accountIds: string[];
  accountConfidence: number | null;
  taxCodeIds: string[];
  taxCodeConfidence: number | null;
  taxCodeJustification: string;
  costCenterIds: string[];
  vatId?: string | null;
  isPaid: boolean;
  smbReviewStatus: InvoiceSmbReviewStatus;
  source: InvoiceSource;
  iban: string | null;
  isSingleInvoice: boolean;
  tags?: InvoiceTag[];
  // Reconcilation AI trigger flag
  reconTriggered?: boolean;
  paymentAmountToPay: number;
};

export type Polygon = {
  polygonId: string; // UUID type
  invoiceId: string; // UUID type
  lineItemId: string; // UUID type
  coordinates: number[];
  pageNumber: number;
  fieldName: string;
  content: string;
};

export type LineItemDetails = {
  lineItem: LineItem;
  lineItemDetailsId: string; // UUID type
  expenseAccount: AccountDetails;
  taxCodeConfidence?: number | undefined;
  taxCode: TaxCode;
};

export type CompanyUser = {
  userId: string;
  name: string;
  email: string;
  systemRole: string;
  companyRole: string;
};

export type UpdateCompanyAccessUsersResponse = {
  success: boolean;
  message: string;
  addedUsers: string[];
  removedUsers: string[];
};

export type Company = {
  companyId: string; // UUID type
  companyName: string;
  companySlug: string;
  vatId: string;
  status: string;
  hasCostCenters: boolean;
  diy: boolean;
  paymentEnabled: boolean;
  ocrReviewRequired: boolean;
  owner?: CompanyUser;
  users?: CompanyUser[];
  accountingFirmId?: string;
  vorsteuerabzug: boolean;
  invoiceInboxEnabled: boolean;
  fiscalYearStartDay?: number;
  fiscalYearStartMonth?: number;
  thirteenBType?: "CONSTRUCTION" | "STANDARD" | "CLEANING";
  createdAt?: string;
  noGeneratedBookingTextInExportPayables?: boolean;
  noGeneratedBookingTextInExportReceivables?: boolean;
  useCustomerNumberAsDebtorAccount?: boolean;
  creditorAccountStart?: string;
};

export type LineItem = {
  expenseAccount: AccountDetails | undefined;
  accountId: string | null | undefined; // UUID type
  lineItemId: string; // UUID type
  aiDescription: string | undefined;
  description?: string;
  amount: number | undefined;
  nettoAmount: number | undefined;
  quantity: number | undefined;
  invoiceId: string | undefined; // UUID type
  name: string | undefined;
  vatRate: number | undefined;
  startDate: Date | undefined;
  taxCodeConfidence?: number | undefined;
  endDate: Date | undefined;
  accountReviewed: boolean | undefined;
  accountConfidence: number | undefined;
  transactions: Transaction[] | undefined;
  accountJustification: string | undefined;
  similarLineItems: LineItem[] | undefined;
  accountSuggestions: AccountDetails[] | undefined;
  taxCode?: TaxCode | undefined;
  index: number | undefined;
  invoice: InvoiceData | undefined;
  costCenterId: string | null | undefined;
  deletedAt?: Date | undefined | null;
  buJustification?: string | undefined;
  taxCodeId?: string | null | undefined;
  bookingText?: string | undefined;
  bookingTextManuallyUpdated?: boolean;
  createdBy: string | null;
  createdAt: string | null;
  unlinked: string | null;
};

export type TaxCode = {
  taxCodeId: string; // UUID type
  name: string;
  description: string;
  datevBu: string;
  vatRate: number;
  automatic: boolean;
};

export enum BankTxnAttachmentType {
  MANUAL = "manual",
  AUTOMATIC = "automatic",
  LOST = "lost",
  NO_INVOICE = "no-invoice",
}

export enum SmbAttachmentStatus {
  ATTACHED = "attached",
  ATTACHMENT_SUGGESTED = "attachment_suggested",
  ATTACHED_ZERO_TOUCH = "attached_zero_touch",
  NOT_ATTACHED = "not_attached",
  NOT_REQUIRED = "not_required",
  NOT_REQUIRED_ZERO_TOUCH = "not_required_zero_touch",
  NOT_REQUIRED_SUGGESTED = "not_required_suggested",
  LOST = "lost",
}

export enum BankTxnStatus {
  NOT_REVIEWED = "not_reviewed",
  REVIEWED = "reviewed",
  REVIEWED_ZERO_TOUCH = "reviewed_zero_touch",
  EXPORT_FAILED = "export_failed",
  EXPORTED = "exported",
  EXPORTING = "exporting",
}

export enum SmbConversationStatus {
  NONE = "none",
  OPEN = "open",
  CLOSED = "closed",
}

export type InvoiceTransaction = {
  invoiceId: InvoiceData["invoiceId"];
  invoiceType: InvoiceData["type"];
  invoiceNo: InvoiceData["invoiceNo"];
  invoiceDate: InvoiceData["invoiceDate"];
  totalAmount: InvoiceData["totalAmount"];
  currency: InvoiceData["currency"];
  supplierName: InvoiceData["supplierName"];
  status: InvoiceData["status"];
  journalEntries: JournalEntry[];
  matchedAt: string;
};

export type Transaction = {
  bankTxnId: string;
  tenantId: string;
  debitAccount: string;
  creditAccount: string;
  bookingText: string;
  amount: number;
  vatRate: number;
  date: Date;
  postingDate: Date;
  createdAt: Date;
  bookingDate: Date;
  currency: string;
  debitAccountDetails: AccountDetails;
  creditAccountDetails: AccountDetails;
  counterpartyName: string;
  counterpartyIban: string;
  bankConnection: BankConnection;
  purpose: string;
  side: TransactionSide;
  executionDate: Date;
  status: string;
  invoices: InvoiceTransaction[];
  smbAttachmentStatus: SmbAttachmentStatus;
  smbConversationStatus: SmbConversationStatus;
  originalAmount: number;
  originalCurrency: string;
  valueDate?: Date;
  bankAccount: BankAccount;
  journalEntries: JournalEntry[];
};

export enum TransactionSide {
  DEBIT = "debit",
  CREDIT = "credit",
}

export enum AccountType {
  ASSET = "ASSET",
  EXPENSE = "EXPENSE",
  GWG = "GWG",
  REVENUE = "REVENUE",
}

export type AccountDetails = {
  accountId: string; // UUID type
  accountName: string;
  accountNo: string;
  accountDescription: string;
  accountType: AccountType;
  automatic: boolean;
  parentAccountId: string | null;
  taxCodeId: string | null;
  faktor2: boolean;
  min: number | null;
  max: number | null;
  buExport: boolean;
  serviceDateExport: boolean;
};

export type AccountsQueryResponse = {
  accounts: AccountDetails[];
};

export type UpdateInvoiceInput = {
  invoiceText: string;
  aiSummary: string;
  totalAmount: number;
  currency: string;
  dueDate: Date;
  invoiceDate: Date;
  invoiceNo: string;
  invoiceSource: string;
  fileBlobName: string;
  supplierId: string;
  status: string;
  invoiceId: string;
  vatId?: string | null;
};

export type Address = {
  [key: string]: any;
  addressId: string | null | undefined;
  addressLine1: string | null | undefined;
  city: string | null | undefined;
  state: string | null | undefined;
  postcode: string | null | undefined;
  country: string | null | undefined;
};

export type PaymentDetails = {
  [key: string]: any;
  paymentDetailsId: string | null | undefined;
  bankName: string | null | undefined;
  IBAN: string | null | undefined;
  BIC: string | null | undefined;
  bankAccount: string | null | undefined;
  bankCode: string | null | undefined;
  bankCountry: string | null | undefined;
};

export type Supplier = {
  [key: string]: any;
  supplierId: string | null | undefined;
  supplierName: string | null | undefined;
  status: string | null | undefined;
  address: Address | null | undefined;
  paymentDetails: PaymentDetails | null | undefined;
  datevAccountNumber: string | null | undefined;
  vatId: string | null | undefined;
  parentSupplierId: string | null | undefined;
  validationErrors: Record<string, string>[] | null | undefined;
  type: "creditor" | "debtor" | null | undefined;
  customerNumber: string | null | undefined;
  promptText: string | null | undefined;
};

export type SuppliersQueryResponse = {
  suppliers: Supplier[];
};

export type Tags = {
  tagId: string;
  tagName: string;
};

export type SimilarInvoice = {
  invoiceId: string; // UUID type
  invoiceText: string;
  aiSummary: string;
  totalAmount: string;
  currency: string;
  dueDate: Date;
  invoiceDate: Date;
  invoiceNo: string;
  invoiceSource: string;
  fileBlobName: string;
  supplierId: string | null; // UUID type
  status: InvoiceStatus;
  createdAt: Date;
  lineItems: LineItem[];
  isNettoInvoice: boolean;
  supplier: Supplier | null;
  supplierMatch: boolean;
  polygons: Polygon[];
  split: boolean;
  reviewType: InvoiceReviewType;
  ocrReviewed: boolean;
  createDatevPayment: boolean;
  accounts: AccountDetails[];
  lineItemDetails: LineItemDetails[];
};

export type InvoiceActivityChange = {
  path: string[];
  type: string;
  value: string;
  oldValue: string;
};

export type InvoiceActivity = {
  invoiceActivityId: string;
  createdAt: Date;
  userId: string;
  user: {
    name: string;
    avatar: string;
  };
  changes: InvoiceActivityChange[];
  activityType: "create" | "update";
};

export type BackgroundJob<D = Record<string, unknown>, T = unknown> = {
  jobId: string;
  jobName: string;
  status: string;
  timestamp: Date;
  sessionId: string;
  progress: number;
  meta: { label?: string } & Record<string, string>;
  data: D;
  returnvalue: {
    value: T;
  };
};

export type Payment = {
  bankTxnId: string;
  tenantId: string;
  status: PaymentEventStatus | null;
  amount: number | null;
  currency: string | null;
  createdAt: string;
  recipientName: string | null;
  recipientIban: string | null;
  purpose: string | null;
  executionDate: string | null;
  invoices: {
    invoiceId: string;
    invoiceNo: string;
    totalAmount: number;
  }[];
};

export type DraftPayment = {
  draftPaymentId: string;
  tenantId: string;
  status: DraftPaymentStatus | null;
  amount: number | null;
  rawAmount: number | null;
  discount: number | null;
  currency: string | null;
  createdAt: string;
  recipientName: string | null;
  recipientIban: string | null;
  purpose: string | null;
  fullPurpose: string | null;
  executionDate: Date | null;
  invoices: {
    invoiceId: string;
    invoiceNo: string;
    totalAmount: number;
  }[];
};

export type BankAccount = {
  bankAccountId: string;
  finapiAccountId: string;
  bankConnectionId: string;
  name: string;
  accountNumber: string;
  type: BankAccountType;
  iban: string;
  defaultAccount: boolean;
  maxCollectivePayments: number;
  balance: number;
  currency: string;
  journalAccounts?: {
    journalAccountId: string;
    name: string;
    number: string;
    entityId: string;
  }[];
  bankConnection?: BankConnection;
};

export type BankConnection = {
  bankConnectionId: string;
  shouldAddFints: boolean;
  finapiBankId: number;
  bankName: string;
  connectionName?: string;
  iban: string;
  createdAt: string;
  updatedAt: string;
  metadata: {
    id: number;
    bank: Bank;
  };
  accounts: BankAccount[];
  reauthRequired: boolean;
  txnImportEnabled: boolean;
};

export type InvoiceComment = {
  invoiceCommentId: string;
  invoiceId: string;
  tenantId: string;
  userId: string;
  comment: string;
  createdAt: string;
  updatedAt: string;
  user: {
    userId: string;
    name: string;
    email: string;
    image: string | null;
  };
};

export enum PaymentEventStatus {
  DRAFT = "draft",
  CONFIRMED = "confirmed",
  INITIATION_STARTED = "initiation_started", // Initiation started means that the webform has been initiated
  INITIATION_COMPLETED = "initiation_completed", // Completed means that the webform has been completed sucessfully. We currently have no way to know if the payment was actually successful or not.
  INITIATION_FAILED = "initiation_failed", // Failed means that the webform has been completed with errors. We currently have no way to know if the payment was actually successful or not.
  DELETED = "deleted",
}

export enum DraftPaymentStatus {
  DRAFT = "draft",
  INITIATED = "initiated",
}

export enum LineItemCreatedBy {
  Manual = "manual",
  Extraction = "extraction",
}

type LoginCredential = {
  label: string;
  isSecret: boolean;
  isVolatile: boolean;
  isMandatory: boolean;
};

type AisCapabilities = {
  standingOrdersDownload: boolean;
  pendingTransactionsDownload: boolean;
};

type PaymentCapabilities = {
  sepaDirectDebit: boolean;
  sepaMoneyTransfer: boolean;
  sepaInstantMoneyTransfer: boolean;
  sepaCollectiveMoneyTransfer: boolean;
  sepaFutureDatedMoneyTransfer: boolean;
  sepaStandingOrder: boolean;
  domesticMoneyTransfer?: boolean;
  domesticCollectiveMoneyTransfer?: boolean;
  domesticFutureDatedMoneyTransfer?: boolean;
};

type PaymentConstraints = {
  sepaMoneyTransfer?: {
    mandatoryFields: {
      counterpartName: boolean;
    };
  };
};

type TppAuthenticationGroup = {
  id: number;
  name: string;
};

type BankInterface = {
  bankingInterface: "WEB_SCRAPER" | "XS2A" | "FINTS_SERVER";
  tppAuthenticationGroup?: TppAuthenticationGroup;
  loginCredentials: LoginCredential[];
  properties: string[];
  loginHint?: string;
  health: number;
  lastCommunicationAttempt?: string;
  lastSuccessfulCommunication?: string;
  isAisSupported: boolean;
  isPisSupported: boolean;
  aisCapabilities: AisCapabilities;
  paymentCapabilities: PaymentCapabilities;
  paymentConstraints?: PaymentConstraints;
  aisAccountTypes: string[];
};

type ImageAsset = {
  url: string;
};

type BankGroup = {
  id: number;
  name: string;
  logo: ImageAsset;
  icon: ImageAsset;
};

export type Bank = {
  id: number;
  name: string;
  bic: string;
  blz: string;
  location: string;
  city: string;
  isTestBank: boolean;
  popularity: number;
  interfaces: BankInterface[];
  bankGroup: BankGroup;
  isBeta: boolean;
  logo?: ImageAsset;
  icon?: ImageAsset;
  shouldAddFints: boolean;
};

export enum BankAccountType {
  CHECKING = "Checking",
  SAVINGS = "Savings",
  CREDIT_CARD = "CreditCard",
  SECURITY = "Security",
  LOAN = "Loan",
  MEMBERSHIP = "Membership",
  BAUSPAREN = "Bausparen",
}

export type EmailForwarding = {
  emailForwardingId: string;
  tenantId: string;
  forwardingAddress: string;
  isActive: boolean;
  alias: string | null;
  description: string | null;
  createdAt: Date;
  updatedAt: Date;
};

export type WhatsAppForwarding = {
  whatsappForwardingId: string;
  tenantId: string;
  phoneNumber: string;
  name: string;
  onboardingState: "pending" | "completed";
  createdAt: Date;
  updatedAt: Date;
};

export type Pagination = {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  nextPage: number;
  previousPage: number;
};

export type KanzleilandIntegration = {
  integrationId: string;
  externalId: string;
  integrationName: string;
  integrationAuth: {
    userToken: string;
    targetToken: string;
  };
  integrationConfig: {
    folderIds: {
      folderId: string;
      folderPath: string;
      type: "kanzlei" | "mandant";
    }[];
    importDate: Date;
  };
  tenantId: string;
  createdAt: string;
  updatedAt: string;
};

export type InvoiceSearchResult = {
  invoiceId: string;
  invoiceNo: string;
  status: InvoiceStatus;
  invoiceDate: Date;
  totalAmount: number;
  supplierName: string;
  invoiceType: InvoiceType;
  currency: string;
  transactions: Transaction[];
  journalEntries: JournalEntry[];
};

export type ParsedCostCenterRecord = {
  identifier: string;
  name: string;
  description?: string;
};

export type ParsedAccountRecord = Pick<
  AccountDetails,
  | "accountNo"
  | "accountName"
  | "accountDescription"
  | "accountType"
  | "faktor2"
  | "min"
  | "max"
  | "buExport"
  | "automatic"
> & {
  parentAccountNo?: string;
  bu1?: string;
  bu3?: string;
};

export type InvoiceDetails = {
  invoiceDetailsId: string;
  invoice: TableInvoiceFragment;
  lineItems: Pick<LineItem, "lineItemId" | "buJustification" | "accountId">[];
  tags: InvoiceTag[];
  payments: Payment[];
  draftPayments: DraftPayment[];
  supplier: Supplier;
  exports: ExportHistoryType[];
  transactions: Transaction[];
};

export enum JOURNAL_LINE_ACCOUNT_TYPE {
  ASSET = "ASSET",
  LIABILITY = "LIABILITY",
  EQUITY = "EQUITY",
  REVENUE = "REVENUE",
  EXPENSE = "EXPENSE",
}

export type JournalAccount = {
  __typename?: "JournalAccount";
  journalAccountId: string;
  tenantId: string;
  number: string | null;
  accountType: JOURNAL_LINE_ACCOUNT_TYPE | null;
  name: string | null;
  entity:
    | ({ __typename: "Account" } & AccountDetails)
    | ({ __typename: "Supplier" } & Supplier)
    | ({ __typename: "BankAccount" } & BankAccount)
    | null;
  accountEntity: AccountDetails | null;
  entityType: AccountEntityType | null;
  entityId: string | null;
  supplier: Supplier | null;
  currency: string | null;
  account: AccountDetails | null;
  bankAccount: BankAccount | null;
};

export type JournalLine = {
  journalLineId: string;
  journalEntryId: string;
  account: string | null;
  taxCodeId: string | null;
  costCenterId: string | null;
  debit: number | null;
  credit: number | null;
  currency: string | null;
  number: number | null;
  tenantId: string;
  createdAt: Date;
  updatedAt: Date;
  deletedAt: Date | null;
  journalAccount: JournalAccount;
  originalAmount: number | null;
  originalCurrency: string | null;
  taxCode: TaxCode | null;
  costCenter: CostCenters | null;
};

export enum JOURNAL_ENTRY_STATUS {
  DRAFT = "draft",
  POSTED = "posted",
}

export type JournalEntry = {
  journalEntryId: string;
  journalId: string;
  tenantId: string;
  timestamp: Date;
  status: JOURNAL_ENTRY_STATUS;
  documentReference: string;
  documentType: string | null;
  documentNumber: string | null;
  bookingText: string | null;
  number: number | null;
  postingEventId: string | null;
  createdBy: string | null;
  createdAt: Date;
  updatedAt: Date;
  deletedAt: Date | null;
  document:
    | ({ __typename: "Invoice" } & TableInvoiceFragment)
    | ({ __typename: "Transaction" } & Transaction)
    | null;
  journalLines: JournalLine[];
};

// ***************** Ledger Account Period Types *****************

export interface InvoiceSummary {
  invoiceId: string;
  invoiceNo: string | null;
  aiSummary: string | null;
  totalAmount: number;
  invoiceDate: Date | null;
  dueDate: Date | null;
}

export interface CounterpartyInfo {
  entityId: string;
  entityName: string;
  entityType: string;
}

export interface AccountPeriodEntry {
  journalEntryId: string;
  timestamp: Date;
  bookingText: string | null;
  documentReference: string | null;
  debit: number;
  credit: number;
  balance: number;
  invoice: InvoiceSummary | null;
  counterparty: CounterpartyInfo | null;
}

export interface PeriodBalance {
  periodKey: string;
  periodLabel: string;
  balance: number;
  debitTotal: number;
  creditTotal: number;
  entryCount: number;
  startDate: Date;
  endDate: Date;
}

export interface AccountPeriodBalance {
  journalAccountId: string;
  accountNumber: string;
  accountName: string;
  accountType: string;
  entityType: string | null;
  periodBalances: PeriodBalance[];
  totalBalance: number;
  hasActivity: boolean;
}

// ***************** Dashboard Aggregation Types *****************

export interface AccountBreakdown {
  accountId: string;
  accountName: string;
  accountNumber: string;
  amount: number;
}

export interface CounterpartyBreakdown {
  entityId: string;
  entityName: string;
  amount: number;
}

export interface TimeSeriesDataPoint {
  period: string;
  date: Date;
  incomeByAccount: AccountBreakdown[];
  expensesByAccount: AccountBreakdown[];
  totalIncome: number;
  totalExpenses: number;
}

export interface TimeSeriesCounterpartiesDataPoint {
  period: string;
  date: Date;
  incomeByCounterparty: CounterpartyBreakdown[];
  expensesByCounterparty: CounterpartyBreakdown[];
  totalIncome: number;
  totalExpenses: number;
}

export interface TopCustomer {
  entityId: string;
  entityName: string;
  entityType: string;
  totalAmount: number;
}

export interface DashboardAggregations {
  timeSeriesByAccount: TimeSeriesDataPoint[];
  timeSeriesByCounterparty: TimeSeriesCounterpartiesDataPoint[];
  topCustomers: TopCustomer[];
  topSuppliers: TopCustomer[];
}

export type EntityCounts = {
  total: number;
  invoices: number;
  bankTransactions: number;
  suppliers: number;
  uploaded: number;
};

export type EntityExportInvoice = {
  invoiceId: string;
  invoiceNo: string;
  totalAmount: number;
};

export type EntityExportBankTransaction = {
  bankTransactionId: string;
  amount: number;
  counterpartyName: string;
};

export type EntityExportSupplier = {
  supplierId: string;
  supplierName: string;
};

export type EntityExport = {
  entityId: string;
  entityType: string;
  uploaded: boolean;
  entity:
    | EntityExportInvoice
    | EntityExportBankTransaction
    | EntityExportSupplier;
};

export type DatevExport = {
  datevExportId: string;
  state: string;
  exportType: string;
  exportedAt: Date;
  stackName: string | null;
  entityCounts: EntityCounts;
  entityExports: EntityExport[];
};
